import { useState } from 'react';
import './values.css';
import Header from '../components/Navbar/Navbar.js';
import Footer from '../components/Footer/Footer.js';
import Seo from '../components/Seo';
import { images } from '../config/imagePaths';

const values = [
  {
    label: "Trust",
    blurb: "We build relationships through transparency, reliability, and integrity.",
    description:
      "Trust is our starting point. We set clear expectations, do what we say we'll do, and communicate with honesty. This creates safe spaces where people speak up, learn, and grow."
  },
  {
    label: "Empowerment",
    blurb: "We equip people with skills and mindset to succeed independently.",
    description:
      "Our goal is to make ourselves less necessary over time. We coach decision-making, provide useful tools, and design support systems that let people lead with confidence."
  },
  {
    label: "Growth",
    blurb: "We value learning and development — personally and professionally.",
    description:
      "Progress beats perfection. We reflect, iterate, and celebrate small wins while building durable habits that move careers and teams forward."
  },
  {
    label: "Authenticity",
    blurb: "We encourage real, honest communication in everything we do.",
    description:
      "No corporate jargon. We speak plainly, listen deeply, and welcome constructive disagreement — because clarity and candor create momentum."
  },
  {
    label: "Support",
    blurb: "We lift each other through mentorship and active listening.",
    description:
      "People thrive with the right scaffolding. We pair feedback with encouragement, provide accountability, and make it easy to ask for help."
  }
];

const segmentColors = [
  '#012f27',
  '#024437',
  '#035e4c',
  '#047a62',
  '#024437',
];

/* Build a donut‑segment SVG path */
function arcPath(cx, cy, outerR, innerR, startDeg, endDeg) {
  const rad = (d) => (d * Math.PI) / 180;
  const ox = (r, a) => cx + r * Math.cos(rad(a));
  const oy = (r, a) => cy + r * Math.sin(rad(a));
  const large = endDeg - startDeg > 180 ? 1 : 0;
  return [
    `M ${ox(outerR, startDeg)} ${oy(outerR, startDeg)}`,
    `A ${outerR} ${outerR} 0 ${large} 1 ${ox(outerR, endDeg)} ${oy(outerR, endDeg)}`,
    `L ${ox(innerR, endDeg)} ${oy(innerR, endDeg)}`,
    `A ${innerR} ${innerR} 0 ${large} 0 ${ox(innerR, startDeg)} ${oy(innerR, startDeg)}`,
    'Z',
  ].join(' ');
}

function WhatWeValue() {
  const [activeIndex, setActiveIndex] = useState(0);
  const cx = 240, cy = 240, outerR = 215, innerR = 110;
  const gap = 2.5;
  const slice = 360 / values.length;

  const segments = values.map((v, i) => {
    const start = i * slice - 90 + gap / 2;
    const end = (i + 1) * slice - 90 - gap / 2;
    const mid = (start + end) / 2;
    const labelR = (outerR + innerR) / 2;
    const rad = (d) => (d * Math.PI) / 180;
    return {
      ...v,
      path: arcPath(cx, cy, outerR, innerR, start, end),
      lx: cx + labelR * Math.cos(rad(mid)),
      ly: cy + labelR * Math.sin(rad(mid)),
      color: segmentColors[i],
    };
  });

  const active = values[activeIndex];

  return (
    <>
      <Seo
        title="What We Value"
        description="The core values that drive AOK Communications: trust, growth, and authentic communication."
      />
      <Header />

      <section className="values-hero page-content reveal">
        <div className="values-hero-text">
          <span className="values-hero-eyebrow">What We Value</span>
          <h1>The principles behind everything we do</h1>
          <p>
            At AOK Communications, our work is guided by five core values. They shape how we
            coach, collaborate, and create — and they keep us human, honest, and focused on the
            people we serve.
          </p>
        </div>
        <div className="values-hero-image hover-lift">
          <img src={images.values.hero} alt="The AOK Communications team" />
        </div>
      </section>

      <div className="hero-divider" aria-hidden="true"></div>

      <section className="values-wheel-section reveal-scale">
        <div className="values-section-heading">
          <h2>Explore our values</h2>
          <p>Select a segment to learn what each value means in practice.</p>
        </div>
        <div className="values-wheel-layout">
          {/* Donut chart */}
          <div className="wheel-wrap">
            <svg viewBox="0 0 480 480" className="values-wheel">
              {segments.map((seg, i) => (
                <g key={seg.label} onClick={() => setActiveIndex(i)} style={{ cursor: 'pointer' }}>
                  <path
                    d={seg.path}
                    fill={seg.color}
                    className={`wheel-seg ${activeIndex === i ? 'active' : ''}`}
                  />
                  <text
                    x={seg.lx}
                    y={seg.ly}
                    textAnchor="middle"
                    dominantBaseline="central"
                    className="wheel-label"
                  >
                    {seg.label}
                  </text>
                </g>
              ))}
              {/* White centre */}
              <circle cx={cx} cy={cy} r={innerR - 6} fill="white" />
              <text
                x={cx}
                y={cy - 8}
                textAnchor="middle"
                dominantBaseline="central"
                className="wheel-center-top"
              >
                AOK
              </text>
              <text
                x={cx}
                y={cy + 14}
                textAnchor="middle"
                dominantBaseline="central"
                className="wheel-center-bottom"
              >
                Values
              </text>
            </svg>
          </div>

          {/* Detail panel */}
          <div className="values-detail" key={activeIndex}>
            <h2>{active.label}</h2>
            <p className="detail-blurb">{active.blurb}</p>
            <p className="detail-desc">{active.description}</p>
          </div>
        </div>
      </section>

      <section className="values-bottom reveal">
        <div className="values-section-heading">
          <h2>What guides us</h2>
          <p>Our foundations, our direction, and the reason behind it all.</p>
        </div>
        <div className="values-bottom-grid stagger-children">
          <div className="values-bottom-card hover-lift">
            <h2>Our Foundations</h2>
            <p>At AOK Communications, our foundation is built on five powerful principles that guide every interaction, service, and strategy we deliver.</p>
          </div>
          <div className="values-bottom-card hover-lift">
            <h2>Our Direction</h2>
            <p>We envision a world where confident communication is the norm. Through mentorship and training, we help professionals find clarity in their voice.</p>
          </div>
          <div className="values-bottom-card hover-lift">
            <h2>Why We Do It</h2>
            <p>Our mission is to elevate individuals through practical, empowering support — making communication human again: bold, clear, and honest.</p>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}

export default WhatWeValue;
